// src/utils/xlsx.ts
import ExcelJS from 'exceljs';
import { CsvColumn } from './csv';
import { AllPointOfInterestTemplate, TemplateFieldTypeEnum } from '@/types';
import {
  POI_CSV_TEMPLATE_COMMON_HEADERS,
  CSV_TEMPLATE_FREE_TEXT_LABEL,
  PIPE_SPEC_FIELD_CONFIG,
  BulkPipeSpecUploadCsvHeadersEnum,
} from '@/constants';

type XlsxColumn = CsvColumn;

export type StatusOption = { value: string; label: string };

type XlsxProcessorConstructor = {
  columns: XlsxColumn[];
  sheetName?: string;
};

// Define a type for cell data
type CellValue = string | number | boolean | null | undefined;

/**
 * Represents an XLSX processor that provides methods for working with Excel data.
 */
export class XlsxProcessor {
  /**
   * An array of XLSX columns, each containing a label and an array of data values.
   */
  private columns: { label: string; data: CellValue[] }[];

  /**
   * The name of the worksheet.
   */
  private sheetName: string;

  /**
   * Creates a new XlsxProcessor instance.
   * @param {XlsxProcessorConstructor} options - The options to initialize the XlsxProcessor with.
   */
  constructor({
    columns,
    sheetName = 'Sheet1',
  }: {
    columns: { label: string; data: CellValue[] }[];
    sheetName?: string;
  }) {
    this.columns = columns;
    this.sheetName = sheetName;
  }

  /**
   * Generates an Excel workbook from the data.
   * @returns {Promise<ExcelJS.Workbook>} The Excel workbook.
   */
  async createWorkbook(): Promise<ExcelJS.Workbook> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.sheetName);

    // Add headers
    worksheet.columns = this.columns.map((column) => ({
      header: column.label,
      key: column.label,
      width: 20,
    }));

    // Add data rows
    const maxRows = Math.max(...this.columns.map((column) => column.data.length));
    for (let i = 0; i < maxRows; i++) {
      const row: Record<string, CellValue> = {};
      this.columns.forEach((column) => {
        row[column.label] = i < column.data.length ? column.data[i] : '';
      });
      worksheet.addRow(row);
    }

    return workbook;
  }

  /**
   * Downloads the Excel data as an XLSX file.
   * @param {string} filename - The name of the file to download.
   */
  async download(filename: string): Promise<void> {
    const workbook = await this.createWorkbook();

    // In browser environment
    if (typeof window !== 'undefined') {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(url);
    } else {
      // In Node.js environment (for testing)
      await workbook.xlsx.writeFile(filename);
    }
  }

  /**
   * Loads new Excel data, replacing the existing columns.
   * @param {XlsxProcessorConstructor} options - The options containing new columns to load.
   */
  load({ columns, sheetName }: XlsxProcessorConstructor): void {
    this.columns = columns;
    if (sheetName) this.sheetName = sheetName;
  }

  /**
   * Retrieves a specific Excel column by its label.
   * @param {string} label - The label of the column to retrieve.
   * @returns {XlsxColumn | undefined} The Excel column with the specified label, or undefined if not found.
   */
  getColumn(label: string): { label: string; data: CellValue[] } | undefined {
    return this.columns.find((column) => column.label === label);
  }

  /**
   * Adds a new column to the Excel data.
   * @param {string} label - The label for the new column.
   * @param {Array<string|number|boolean|null|undefined>} data - An array of data values for the new column.
   */
  addColumn(label: string, data: Array<string | number | boolean | null | undefined>): void {
    this.columns.push({ label, data });
  }

  /**
   * Removes an Excel column by its label.
   * @param {string} label - The label of the column to remove.
   */
  removeColumn(label: string): void {
    this.columns = this.columns.filter((column) => column.label !== label);
  }
}

export const downloadArrayAsXlsx = async (headers: string[], filename: string): Promise<void> => {
  const xlsx = new XlsxProcessor({
    columns: headers.map((header) => ({ label: header, data: [] })),
  });

  await xlsx.download(filename);
};

/**
 * Downloads a pipe spec template with instruction rows beneath each column header
 * Uses PIPE_SPEC_FIELD_CONFIG for scalable field configuration
 * @param headers - Array of column headers (from BulkPipeSpecUploadCsvHeadersEnum)
 * @param filename - Name of the file to download
 */
export const downloadPipeSpecTemplateAsXlsx = async (
  headers: string[],
  filename: string
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Pipe Specs');

  // Set column widths
  worksheet.columns = headers.map((header) => ({
    key: header,
    width: 25,
  }));

  // Add the header row first (row 1) - required for parser
  const headerRow = worksheet.addRow(
    headers.reduce((obj, header) => {
      obj[header] = header;
      return obj;
    }, {} as Record<string, string>)
  );

  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD9E1F0' },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
  headerRow.height = 25;

  // Add instruction row beneath each column header (row 2)
  // Uses PIPE_SPEC_FIELD_CONFIG for scalable configuration
  const instructionRow = worksheet.addRow({});
  headers.forEach((header, index) => {
    const cell = instructionRow.getCell(index + 1);
    const fieldConfig = PIPE_SPEC_FIELD_CONFIG[header as BulkPipeSpecUploadCsvHeadersEnum];
    const instructionText = fieldConfig?.instruction || '';

    cell.value = instructionText;
    cell.font = { size: 9, italic: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF5F5F5' },
    };
    cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
  });
  instructionRow.height = 30;

  // In browser environment
  if (typeof window !== 'undefined') {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  } else {
    // In Node.js environment (for testing)
    await workbook.xlsx.writeFile(filename);
  }
};

export class XlsxTemplateProcessor {
  private template: AllPointOfInterestTemplate[0];
  private customStatusOptions?: StatusOption[];

  constructor(template: AllPointOfInterestTemplate[0], customStatusOptions?: StatusOption[]) {
    this.template = template;
    this.customStatusOptions = customStatusOptions;
  }

  // -- Private helpers --
  private getVisibleFields(): { name: string }[] {
    return this.template.fields.filter((field) => !field.isHidden).map((f) => ({ name: f.name }));
  }

  private createWorkbookAndSheet(): { workbook: ExcelJS.Workbook; worksheet: ExcelJS.Worksheet } {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.template.name);
    return { workbook, worksheet };
  }

  private buildHeaders(fields: { name: string }[]): string[] {
    const headers = fields.map((field) => field.name);
    headers.unshift(...POI_CSV_TEMPLATE_COMMON_HEADERS.map((header) => header.name));
    return headers;
  }

  private setWorksheetColumns(worksheet: ExcelJS.Worksheet, headers: string[]): void {
    worksheet.columns = headers.map((name) => ({
      header: name,
      key: name,
      width: 20,
    }));
  }

  private setCell(
    rows: Array<Record<string, string>>,
    rowIndex: number,
    header: string,
    value: string
  ) {
    const existing = rows[rowIndex] || {};
    rows[rowIndex] = { ...existing, [header]: value };
  }

  private populateCommonColumn(rows: Array<Record<string, string>>, header: string): boolean {
    const foundCommonColumn = POI_CSV_TEMPLATE_COMMON_HEADERS.find((h) => h.name === header);
    if (!foundCommonColumn) return false;

    // Use custom status labels when available for the Status column
    let effectiveOptions = foundCommonColumn.options ? [...foundCommonColumn.options] : [];
    if (
      header === 'Status' &&
      this.customStatusOptions &&
      this.customStatusOptions.length > 0
    ) {
      effectiveOptions = this.customStatusOptions.map((opt) => opt.label);
    }

    if (effectiveOptions.length > 0) {
      effectiveOptions.forEach((option, index) => {
        const value = foundCommonColumn.required && index === 0 ? `(Required) ${option}` : option;
        this.setCell(rows, index, header, value);
      });
    } else {
      const helper = foundCommonColumn.helperText || '';
      const value = foundCommonColumn.required ? `(Required) ${helper}` : helper;
      this.setCell(rows, 0, header, value);
    }
    return true;
  }

  private populateFieldColumn(rows: Array<Record<string, string>>, header: string): void {
    const field = this.template.fields.find((f) => f.name === header);
    if (!field) return;

    switch (field.type) {
      case TemplateFieldTypeEnum.SingleSelect:
      case TemplateFieldTypeEnum.RadioButton: {
        (field.options || []).forEach((option: string, index: number) => {
          const value = field.isRequired && index === 0 ? `(Required) ${option}` : option;
          this.setCell(rows, index, header, value);
        });
        break;
      }
      default: {
        const value = field.isRequired
          ? `(Required) ${CSV_TEMPLATE_FREE_TEXT_LABEL}`
          : CSV_TEMPLATE_FREE_TEXT_LABEL;
        this.setCell(rows, 0, header, value);
        break;
      }
    }
  }

  private buildRows(headers: string[]): Array<Record<string, string>> {
    const rows: Array<Record<string, string>> = [];

    headers.forEach((header) => {
      // Populate common columns first; if not common, use template field rules
      if (!this.populateCommonColumn(rows, header)) {
        this.populateFieldColumn(rows, header);
      }
    });

    return rows;
  }

  private addRowsToWorksheet(
    worksheet: ExcelJS.Worksheet,
    headers: string[],
    rows: Array<Record<string, string>>
  ): void {
    rows.forEach((row) => {
      const excelRow: Record<string, string> = {};
      headers.forEach((h) => {
        excelRow[h] = row[h] || '';
      });
      worksheet.addRow(excelRow);
    });
  }

  private async saveWorkbook(workbook: ExcelJS.Workbook, filename: string): Promise<void> {
    if (typeof window !== 'undefined') {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(url);
    } else {
      await workbook.xlsx.writeFile(filename);
    }
  }

  // -- Public API --
  async download(filename: string): Promise<void> {
    const fields = this.getVisibleFields();
    const { workbook, worksheet } = this.createWorkbookAndSheet();
    const headers = this.buildHeaders(fields);

    this.setWorksheetColumns(worksheet, headers);

    const rows = this.buildRows(headers);
    this.addRowsToWorksheet(worksheet, headers, rows);

    await this.saveWorkbook(workbook, filename);
  }
}

/**
 * Reads a File object and loads it as an ExcelJS Workbook.
 * @param file - The File object to read
 * @returns Promise<ExcelJS.Workbook> - The loaded workbook
 */
export const loadXlsxFromFile = async (file: File): Promise<ExcelJS.Workbook> => {
  const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Error reading XLSX file'));
    reader.onload = (ev) => {
      const result = ev.target?.result as ArrayBuffer;
      if (!result) {
        reject(new Error('Failed to read file'));
        return;
      }
      resolve(result);
    };
    reader.readAsArrayBuffer(file);
  });

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);
  return workbook;
};

export type ParsedXlsxData = {
  header: string[];
  rows: Record<string, unknown>[];
};

/**
 * Parses an ExcelJS Worksheet into header and row data.
 * Applies sanitization to cell values (removes leading non-alphanumeric characters).
 * @param worksheet - The worksheet to parse
 * @returns ParsedXlsxData - Object containing header array and rows array
 */
export const parseXlsxWorksheet = (worksheet: ExcelJS.Worksheet): ParsedXlsxData => {
  const header: string[] = [];
  worksheet.getRow(1).eachCell((cell) => {
    header.push(cell.value?.toString() || '');
  });

  const rows: Record<string, unknown>[] = [];
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // Skip header row

    const rowData: Record<string, unknown> = {};
    row.eachCell((cell, colNumber) => {
      if (colNumber <= header.length) {
        const raw = cell.value?.toString() || '';
        const sanitized = raw.replace(/^[^a-zA-Z0-9]+/, '');
        rowData[header[colNumber - 1]] = sanitized;
      }
    });

    rows.push(rowData);
  });

  return { header, rows };
};

/**
 * Removes rows from the first worksheet of a workbook based on data row indices.
 * Rows are removed in reverse order to maintain correct indices.
 * Styles and formatting are automatically preserved since we modify the existing workbook.
 *
 * @param workbook - The workbook to modify
 * @param invalidRowIndices - Set of data row indices to remove (0-based, where 0 = first data row after header)
 */
export const removeRowsFromWorkbook = (
  workbook: ExcelJS.Workbook,
  invalidRowIndices: Set<number>
): void => {
  const worksheet = workbook.worksheets[0];
  if (!worksheet) return;

  // Convert data indices (0-based) to Excel row numbers (1-based, +1 for header)
  // Data index 0 = Excel row 2, Data index 1 = Excel row 3, etc.
  const rowNumbersToRemove = Array.from(invalidRowIndices)
    .map((idx) => idx + 2)
    .sort((a, b) => b - a); // Sort descending to remove from bottom up

  // Remove rows from bottom to top to maintain correct indices
  rowNumbersToRemove.forEach((rowNumber) => {
    worksheet.spliceRows(rowNumber, 1);
  });
};

/**
 * Converts a workbook to a Blob for upload.
 * @param workbook - The workbook to convert
 * @returns Promise<Blob> - The workbook as a Blob
 */
export const workbookToBlob = async (workbook: ExcelJS.Workbook): Promise<Blob> => {
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

